import { useMemo, useState } from "react";

import type { ColumnDef } from "@tanstack/react-table";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import {
  Edit3,
  KeyRound,
  Link2,
  Loader2,
  MoreHorizontal,
  Plus,
  ShieldOff,
  UserPlus,
  UserX,
  Users,
} from "lucide-react";

import { AdminUserFormSheet } from "@/features/admin-users/presentation/components/AdminUserFormSheet";
import { ConfirmDialog } from "@/shared/components/data/ConfirmDialog";
import { DataTable } from "@/shared/components/data/DataTable";
import { DataToolbar } from "@/shared/components/data/DataToolbar";
import { Pagination } from "@/shared/components/data/Pagination";
import { StatusPill } from "@/shared/components/data/StatusPill";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useQueryState } from "@/shared/hooks/use-query-state";
import { toast } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/store";
import {
  AccountKind,
  useDeactivateAdminUserMutation,
  useListAdminUsersQuery,
  useListRolesQuery,
  useResetAdminUserMfaMutation,
} from "@/store/apis/adminApi.enhanced";
import type { AdminUser } from "@/store/apis/adminApi.enhanced";
import type { ListAdminUsersApiArg } from "@/store/apis/adminApi.generated";
import { selectAdmin } from "@/store/selectors";

type Sort = NonNullable<ListAdminUsersApiArg["sort"]>;

const DEFAULT_LIMIT = 25;

export function AdminUsersPage() {
  const me = useAppSelector(selectAdmin);
  const { get, set } = useQueryState();

  const search = get("q", "");
  const debouncedSearch = useDebouncedValue(search, 300);
  const roleFilter = get("role", "");
  const activeFilter = get("active", "");
  const kindFilter = get("kind", "");
  const sort = (get("sort", "full_name") || "full_name") as Sort;
  const offset = Number(get("offset", "0")) || 0;
  const limit = Number(get("limit", String(DEFAULT_LIMIT))) || DEFAULT_LIMIT;

  const filtersActive =
    debouncedSearch.trim().length > 0 ||
    Boolean(roleFilter) ||
    activeFilter !== "" ||
    Boolean(kindFilter);

  const { data, isLoading, isFetching, isError, error, refetch } = useListAdminUsersQuery({
    q: debouncedSearch || undefined,
    role: roleFilter || undefined,
    active: activeFilter === "" ? undefined : activeFilter === "1",
    accountKind: (kindFilter as AccountKind) || undefined,
    sort,
    offset,
    limit,
  });

  const rolesQuery = useListRolesQuery();
  const roleLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rolesQuery.data?.items ?? []) m.set(r.id, r.label);
    return m;
  }, [rolesQuery.data]);

  const [deactivate] = useDeactivateAdminUserMutation();
  const [resetMfa] = useResetAdminUserMfaMutation();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const openCreate = () => {
    setEditing(null);
    setSheetOpen(true);
  };
  const openEdit = (u: AdminUser) => {
    setEditing(u);
    setSheetOpen(true);
  };

  const handleDeactivate = async (u: AdminUser) => {
    try {
      await deactivate({ id: u.id }).unwrap();
      toast.success(`Admin ${u.full_name} desactivada`);
    } catch (err) {
      toast.error(err);
      throw err;
    }
  };

  const handleResetMfa = async (u: AdminUser) => {
    try {
      await resetMfa({ id: u.id }).unwrap();
      toast.success(`MFA reseteado para ${u.full_name}. Deberá enrolar en su próximo login.`);
    } catch (err) {
      toast.error(err);
      throw err;
    }
  };

  const columns = useMemo<ColumnDef<AdminUser>[]>(
    () => [
      {
        id: "admin",
        header: "Admin",
        cell: ({ row }) => {
          const u = row.original;
          const initials = getInitials(u.full_name);
          const isMe = u.id === me?.id;
          const isLinked = u.account_kind === AccountKind.Linked;
          return (
            <div className="flex items-center gap-3">
              <div
                aria-hidden
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15"
              >
                <span className="font-display text-[13px] font-medium">{initials}</span>
              </div>
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 truncate text-[13.5px] font-medium text-foreground">
                  <span className="truncate">{u.full_name}</span>
                  {isMe ? (
                    <span className="font-mono text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      Tú
                    </span>
                  ) : null}
                  {isLinked ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-1.5 py-[1px] font-mono text-[9px] font-medium uppercase tracking-[0.22em] text-primary"
                      title="Cuenta vinculada al app móvil"
                    >
                      <Link2 className="h-2.5 w-2.5" />
                      Vinculada
                    </span>
                  ) : null}
                </p>
                <p className="truncate font-mono text-[11.5px] text-muted-foreground">{u.email}</p>
              </div>
            </div>
          );
        },
      },
      {
        id: "role",
        header: "Rol",
        cell: ({ row }) => (
          <StatusPill tone="neutral" dot={false}>
            {roleLabelById.get(row.original.role) ?? row.original.role}
          </StatusPill>
        ),
      },
      {
        id: "status",
        header: "Estado",
        cell: ({ row }) => (
          <StatusPill tone={row.original.active ? "success" : "muted"}>
            {row.original.active ? "Activo" : "Inactivo"}
          </StatusPill>
        ),
      },
      {
        id: "last_login_at",
        header: "Último login",
        cell: ({ row }) => {
          const d = row.original.last_login_at;
          return (
            <span className="font-mono text-[12px] text-muted-foreground">
              {d ? `hace ${formatDistanceToNow(new Date(d), { locale: es })}` : "Nunca"}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Acciones</span>,
        cell: ({ row }) => {
          const u = row.original;
          const isMe = u.id === me?.id;
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <MoreHorizontal className="h-4 w-4" />
                    <span className="sr-only">Acciones</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => openEdit(u)}>
                    <Edit3 className="h-3.5 w-3.5" />
                    Editar
                  </DropdownMenuItem>
                  {!isMe ? (
                    <>
                      <DropdownMenuSeparator />
                      <ConfirmDialog
                        trigger={
                          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                            <KeyRound className="h-3.5 w-3.5" />
                            Resetear MFA
                          </DropdownMenuItem>
                        }
                        title="Resetear MFA"
                        description={
                          <>
                            Se eliminará el factor TOTP de{" "}
                            <strong className="text-foreground">{u.full_name}</strong>. En su
                            próximo login deberá enrolar un nuevo autenticador.
                          </>
                        }
                        confirmLabel="Sí, resetear"
                        tone="primary"
                        icon={<ShieldOff className="h-5 w-5" />}
                        onConfirm={() => handleResetMfa(u)}
                      />
                      {u.active ? (
                        <ConfirmDialog
                          trigger={
                            <DropdownMenuItem
                              onSelect={(e) => e.preventDefault()}
                              className="text-destructive focus:text-destructive"
                            >
                              <UserX className="h-3.5 w-3.5" />
                              Desactivar
                            </DropdownMenuItem>
                          }
                          title="Desactivar admin"
                          description={
                            <>
                              <strong className="text-foreground">{u.full_name}</strong> dejará de
                              poder iniciar sesión. Las sesiones activas se mantienen hasta su
                              próximo refresh.
                            </>
                          }
                          confirmLabel="Sí, desactivar"
                          onConfirm={() => handleDeactivate(u)}
                        />
                      ) : null}
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleLabelById, me?.id],
  );

  return (
    <>
      <PageHeader
        kicker="Operación"
        title="Administradores"
        description="Gestión de las personas con acceso al backoffice de Arya."
        actions={
          <Button size="sm" onClick={openCreate}>
            <UserPlus className="h-3.5 w-3.5" />
            Nuevo admin
          </Button>
        }
      />

      <DataToolbar
        search={search}
        onSearchChange={(next) => set({ q: next, offset: 0 })}
        searchPlaceholder="Buscar por nombre o email…"
        filters={
          <>
            <Select
              value={roleFilter || "all"}
              onValueChange={(v) => set({ role: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[180px] text-[13px]">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                {rolesQuery.data?.items.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={activeFilter || "all"}
              onValueChange={(v) => set({ active: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[140px] text-[13px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="1">Activos</SelectItem>
                <SelectItem value="0">Inactivos</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={kindFilter || "all"}
              onValueChange={(v) => set({ kind: v === "all" ? "" : v, offset: 0 })}
            >
              <SelectTrigger className="h-10 w-[160px] text-[13px]">
                <SelectValue placeholder="Tipo de cuenta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                <SelectItem value={AccountKind.Staff}>Solo staff</SelectItem>
                <SelectItem value={AccountKind.Linked}>Vinculadas del app</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sort ?? "full_name"} onValueChange={(v) => set({ sort: v })}>
              <SelectTrigger className="h-10 w-[180px] text-[13px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_name">Nombre A–Z</SelectItem>
                <SelectItem value="-full_name">Nombre Z–A</SelectItem>
                <SelectItem value="-last_login_at">Login reciente</SelectItem>
                <SelectItem value="-created_at">Creado reciente</SelectItem>
                <SelectItem value="email">Email A–Z</SelectItem>
              </SelectContent>
            </Select>
          </>
        }
        actions={
          isFetching && !isLoading ? (
            <span
              className={cn(
                "inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground",
              )}
            >
              <Loader2 className="h-3 w-3 animate-spin" />
              Actualizando
            </span>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        data={data?.items}
        loading={isLoading}
        error={isError ? error : undefined}
        onRetry={() => void refetch()}
        onRowClick={openEdit}
        getRowId={(row) => row.id}
        filtersActive={filtersActive}
        empty={{
          icon: <Users className="h-6 w-6" />,
          title: "Aún no hay admins",
          description: "Crea el primer administrador del backoffice.",
          actions: (
            <Button onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" />
              Nuevo admin
            </Button>
          ),
        }}
        noResults={{
          actions: (
            <Button
              variant="outline"
              size="sm"
              onClick={() => set({ q: "", role: "", active: "", kind: "", offset: 0 })}
            >
              Limpiar filtros
            </Button>
          ),
        }}
      />

      <Pagination
        total={data?.total ?? 0}
        limit={limit}
        offset={offset}
        onOffsetChange={(next) => set({ offset: next })}
        onLimitChange={(next) => set({ limit: next })}
      />

      <AdminUserFormSheet open={sheetOpen} onOpenChange={setSheetOpen} user={editing} />
    </>
  );
}

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
